import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInventory, useBatchHistory } from '../hooks/useInventory';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const HistoryPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [batchList, setBatchList] = useState<{ id: string; createdAt: string; userName: string }[]>([]);
  const { exportCsv, isLoading: exportLoading } = useInventory();
  
  // Use the paginated batch history hook
  const { data: batchResponse, isLoading, error, refetch } = useBatchHistory(currentPage, 20);

  // Process raw batch data when it changes
  useEffect(() => {
    if (batchResponse?.data) {
      const parsed = batchResponse.data.map((batch) => {
        const userName = extractNameFromBatchId(batch.id);
        return {
          id: batch.id,
          createdAt: new Date(batch.createdAt).toISOString(),
          userName,
        };
      });
      
      setBatchList(parsed);
    }
  }, [batchResponse]);

  // Helper function to extract name from batchId
  const extractNameFromBatchId = (batchId: string): string => {
    if (!batchId) return '';
    const parts = batchId.split('_');
    return parts[0] || '';
  };

  const handleExport = async (batchId: string) => {
    if (!batchId) return;

    try {
      await exportCsv({ batchId });
      toast.success(`CSV exported for batch: ${batchId}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load batch history</p>
          <p className="text-sm text-gray-600 mb-4">{error.message || 'Unknown error occurred'}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const pagination = batchResponse?.pagination;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Inventory History</h1>
          </div>
          
          {/* Results Count */}
          {pagination && (
            <div className="text-sm text-gray-600">
              Showing {((pagination.currentPage - 1) * 20) + 1}-{Math.min(pagination.currentPage * 20, pagination.totalCount)} of {pagination.totalCount} sessions
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <LoadingSpinner text="Loading batch history..." />
          </div>
        ) : (
          /* Batch List */
          batchList.length === 0 && pagination?.totalCount === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
              <div className="max-w-md mx-auto">
                {/* Empty State Icon */}
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                
                {/* Empty State Message */}
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No scanning history found
                </h3>
                <p className="text-gray-500 mb-6">
                  You haven't completed any scanning sessions yet. Start your first session to see the history here.
                </p>
                
                {/* Call to Action Button */}
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Start New Scanning Session
                </button>
                
                {/* Help Text */}
                <p className="text-sm text-gray-400 mt-4">
                  💡 Tip: Complete a scanning session and it will appear in this history list
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Batch List */}
              <div className="space-y-4 mb-8">
                {batchList.map((batch) => (
                  <div
                    key={batch.id}
                    className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      {/* Batch Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                          Scanner: {batch.userName || 'Unknown'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Session Started: {new Date(batch.createdAt).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                        </p>
                        <p className="text-xs font-mono text-gray-500 break-all">
                          Batch ID: {batch.id}
                        </p>
                      </div>

                      {/* Download Button */}
                      <div className="ml-6 flex-shrink-0">
                        <button
                          onClick={() => handleExport(batch.id)}
                          disabled={exportLoading}
                          className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            exportLoading
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                          }`}
                        >
                          {exportLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                              </svg>
                              Exporting...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Download CSV
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white rounded-lg shadow-sm border px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrevPage}
                        className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md ${
                          pagination.hasPrevPage
                            ? 'text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </button>

                      {/* Page Numbers */}
                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-2 text-sm leading-4 font-medium rounded-md ${
                                pageNum === pagination.currentPage
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md ${
                          pagination.hasNextPage
                            ? 'text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )
        )}

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Each CSV download contains all scanned items for that session.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;